import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Graph } from "src/generated/graph";
import { ProductDraftToggle } from "../forms/ProductDraftToggle";
import { Table } from "../layout/tables/Table";
import { Loader } from "./Loader";

const QUERY_PRODUCT = gql`
  query supplierProductRequestList($offset: Int = 0, $filter: FilterSupplierRequest, $stockOnly: Boolean) {
    supplierProductRequestList(offset: $offset, limit: 10, filter: $filter, stockOnly: $stockOnly) {
      data {
        content
        soldQty
        revenue
      }
      page {
        current
        size
        total
      }
    }
  }
`;

interface Props {
  show: boolean;
  setShow: any;
  items: any[];
  setItems: any;
  stockOnly?: boolean;
  onRemove?: any;
}

function ProductField({
  product,
  onCheck,
  checked,
}: {
  product: Graph.SupplierProductRequest;
  onCheck: (e: "T" | "F" | undefined) => void;
  checked?: "T" | "F" | undefined;
}) {
  const [check, setCheck] = useState<"T" | "F" | undefined>(checked);
  const price: number = Number(product.content.saleprice || 0) * (1 - Number(product.content.discount || 0) / 100);
  return (
    <tr>
      <td className="text-center">
        <img
          src={product.content.picture}
          alt=""
          style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }}
        />
      </td>
      <td>
        <small>
          <strong>{product.content.supplier_code}</strong>
          <br />
          <a href={"https://www.l192.com/product/" + product.content.itemid} target="_blank" rel="noopener noreferrer">
            {product.content.code}
          </a>
        </small>
      </td>
      <td>
        <small>Sold Qty: {(product as any).soldQty}</small>
        <br />
        <small>Revenue: ${(product as any).revenue} </small>
      </td>
      <td>
        <small>
          {Number(product.content.discount || 0) > 0 && (
            <span className="text-danger" style={{ textDecoration: "line-through" }}>
              ${product.content.saleprice}
            </span>
          )}
          ${price.toFixed(2)}
        </small>
        <br />
        <small>Discount: {Number(product.content.discount).toPrecision(2)}%</small>
      </td>
      <td>
        <small>{product.content.stock}</small> <br />
        {product.content.remote_stock === "T" && <small className="text-danger">Remote Stock</small>}
      </td>
      <td className="text-right" style={{ width: 100 }}>
        <ProductDraftToggle
          name=""
          value={check}
          onChange={(v) => {
            setCheck(v);
            onCheck(v);
          }}
        />
      </td>
    </tr>
  );
}

export function ProductDraft(props: Props) {
  const { show, setShow, items, setItems } = props;

  const [selected, setSeleted] = useState(false);

  const [offset, setOffset] = useState(0);
  const [offsetInput, setOffsetInput] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("AVAILABLE");
  const [sort, setSort] = useState("INPUTDATE");
  const [order, setOrder] = useState<"ASC" | "DESC" | any>("DESC");
  const [page, setPage] = useState<any>({});

  const { data, loading, error } = useQuery<Graph.Query>(QUERY_PRODUCT, {
    fetchPolicy: "no-cache",
    variables: {
      offset: offset * 10,
      filter: { status: "APPROVED", order, type, search, sort },
      stockOnly: props.stockOnly ? props.stockOnly : undefined,
    },
    onCompleted: (res) => {
      setPage(
        Number(res?.supplierProductRequestList?.page?.total) / Number(res?.supplierProductRequestList?.page?.size),
      );
    },
  });

  const handleClose = () => setShow(false);

  const handlePrevious = () => {
    setOffset(offset === 0 ? 0 : offset - 1);
    setOffsetInput(offsetInput === 1 ? 1 : offsetInput - 1);
  };

  const handleNext = () => {
    setOffset(offset + 1);
    setOffsetInput(offsetInput + 1);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton translate="yes" onAuxClick={() => {}} onAuxClickCapture={() => {}}>
        <Modal.Title>Product</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        <div className="p-3">
          <div className="row">
            <div className="col-md-3">
              <select
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value={"AVAILABLE"}>Available</option>
                <option value={"DISCONTINUE"}>Discontinue</option>
                <option value={"OUT_OF_STOCK"}>Out Of Stock</option>
                <option value={"DISCOUNT_OFFER"}>Discount Offer</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value={"INPUTDATE"}>Date</option>
                <option value={"STOCK"}>Stock</option>
                <option value={"SALE"}>Sale</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control form-control-sm"
                style={{ width: 100 }}
                value={order}
                onChange={(e) => setOrder(e.target.value)}
              >
                <option value="ASC">A - Z</option>
                <option value="DESC">Z - A</option>
              </select>
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="search by code"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.keyCode === 13 && setSearch(searchInput.trim())}
              />
            </div>
          </div>
          <br />
          <label>
            <input
              type="checkbox"
              className="mr-1"
              checked={selected}
              onChange={(e) => {
                setSeleted(e.target.checked);
                if (!!e.target.checked) {
                  setSearch(items.map((x) => x.code).join(","));
                } else {
                  setSearch("");
                }
              }}
            />
            Selected Product Only
          </label>
        </div>
        <div className="row">
          <div className="col-md-12">
            {error && <div className="alert alert-danger">Product list is under maintenance.</div>}
            <Table stripe border grid seperator animation>
              <thead>
                <tr>
                  <th></th>
                  <th>code</th>
                  <th>sale info</th>
                  <th>price</th>
                  <th colSpan={2}>stock</th>
                </tr>
              </thead>
              <tbody>
                {loading || !data
                  ? [...new Array(data)].map((x) => {
                      return (
                        <tr>
                          <td>
                            <Loader type="SQUARE" />
                          </td>
                          <td>
                            <Loader type="LINE" />
                            <br />
                            <Loader type="LINE" />
                          </td>
                          <td>
                            <Loader type="LINE" />
                            <br />
                            <Loader type="LINE" />
                          </td>
                          <td>
                            <Loader type="LINE" />
                          </td>
                          <td>
                            <Loader type="CIRCLE" />
                          </td>
                        </tr>
                      );
                    })
                  : data.supplierProductRequestList?.data!.map((x) => {
                      const index = items
                        .filter((item) => item !== undefined)
                        .findIndex((item) => item.id === x.content.itemid);
                      return (
                        <ProductField
                          product={x}
                          checked={index < 0 ? "F" : "T"}
                          onCheck={(check) => {
                            if (check === "T") {
                              setItems([
                                ...items,
                                {
                                  ...x,
                                  id: x.content.itemid,
                                  image: x.content.picture,
                                  code: x.content.code,
                                },
                              ]);
                            } else {
                              if (props.onRemove) {
                                props.onRemove(items.filter((item) => item.id === x.content.itemid));
                              }
                              setItems(items.filter((item) => item.id !== x.content.itemid));
                            }
                          }}
                        />
                      );
                    })}
              </tbody>
            </Table>
          </div>
          <div className="col-md-12">
            <br />
            <div className="p-3">
              <div className="controls-below-table">
                <div className="table-records-info">
                  Showing records {1 * offsetInput} - {10 * offsetInput} of {page * 10}
                </div>
                <div className="table-records-pages">
                  <ul>
                    <li>
                      {offset === 0 ? (
                        <span>Previous</span>
                      ) : (
                        <button className="btn btn-link p-0" onClick={handlePrevious}>
                          Previous
                        </button>
                      )}
                    </li>
                    <li>
                      <input
                        style={{
                          width: 60,
                          height: 30,
                          borderStyle: "none",
                          textAlign: "center",
                        }}
                        className="text-inputpage"
                        type="number"
                        value={offsetInput}
                        onChange={(e) => {
                          setOffsetInput(Number(e.target.value));
                        }}
                        onBlur={() => {
                          if (Number(offsetInput) === offset) return;
                          setOffset(offsetInput);
                        }}
                        onKeyDown={(e) => e.keyCode === 13 && Number(offsetInput) !== offset && setOffset(offsetInput)}
                      />
                    </li>
                    <li>
                      <button className="btn btn-link p-0" onClick={handleNext}>
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
